import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button, Spinner, Alert } from '../ui';
import { useCountry } from '../../hooks/useCountry';

// Initialize Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

// Security Badge Component
const SecurityBadge = ({ icon, text }) => (
  <div className="flex items-center gap-2 text-sm text-gray-600">
    {icon}
    <span>{text}</span>
  </div>
);

// Order Summary Item
const OrderItem = ({ label, value, highlight }) => (
  <div className={`flex justify-between ${highlight ? 'text-lg font-semibold' : 'text-sm'}`}>
    <span className={highlight ? 'text-gray-900' : 'text-gray-600'}>{label}</span>
    <span className={highlight ? 'text-gray-900' : 'text-gray-900'}>{value}</span>
  </div>
);

const PaymentForm = ({ clientSecret, onSuccess, onError, onCancel, price, currency, productName, email, onEmailChange, showEmailInput, primaryColor }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const { t } = useTranslation();

  const getCurrencySymbol = (curr) => {
    const symbols = { EUR: '€', GBP: '£', USD: '$', CAD: 'CA$', AUD: 'A$' };
    return symbols[curr] || '$';
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Validate email for guest users
    if (showEmailInput && !email) {
      setErrorMessage(t('payment.emailRequired'));
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success`,
          receipt_email: email || undefined,
        },
        redirect: 'if_required',
      });

      if (error) {
        setErrorMessage(error.message);
        setProcessing(false);
        onError && onError(error);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess && onSuccess(paymentIntent);
      }
    } catch (err) {
      setErrorMessage(t('payment.unexpectedError'));
      setProcessing(false);
      onError && onError(err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Email Input for Guest Users */}
      {showEmailInput && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('payment.emailLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder={t('payment.emailPlaceholder')}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl text-base transition-colors"
            style={{ outline: 'none' }}
            onFocus={(e) => { e.target.style.borderColor = primaryColor; e.target.style.boxShadow = `0 0 0 2px ${primaryColor}20`; }}
            onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }}
            required
          />
          <p className="text-xs text-gray-500 mt-1.5">
            {t('payment.emailHelp')}
          </p>
        </div>
      )}

      {/* Payment Element */}
      <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
        <PaymentElement
          options={{
            layout: 'tabs'
          }}
        />
      </div>

      {/* Error Message */}
      {errorMessage && (
        <Alert type="error" dismissible onDismiss={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={processing}
          fullWidth
        >
          {t('payment.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={!stripe || processing}
          loading={processing}
          fullWidth
          style={primaryColor ? { background: primaryColor } : undefined}
        >
          {processing ? t('payment.processing') : `${t('payment.pay')} ${getCurrencySymbol(currency)}${price || '29.99'}`}
        </Button>
      </div>
    </form>
  );
};

// Success Animation Component
const PaymentSuccess = ({ onClose, primaryColor }) => {
  const { t } = useTranslation();

  return (
    <div className="text-center py-8 animate-scale-in">
      {/* Success Checkmark */}
      <div className="relative inline-flex mb-6">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
          <svg className="w-10 h-10 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        {/* Celebration particles */}
        <div className="absolute -top-2 -left-2 w-3 h-3 bg-blue-400 rounded-full animate-ping" style={{ animationDelay: '0s' }} />
        <div className="absolute -top-1 -right-3 w-2 h-2 bg-amber-400 rounded-full animate-ping" style={{ animationDelay: '0.2s' }} />
        <div className="absolute -bottom-1 -left-3 w-2 h-2 bg-rose-400 rounded-full animate-ping" style={{ animationDelay: '0.4s' }} />
        <div className="absolute -bottom-2 -right-2 w-3 h-3 bg-indigo-400 rounded-full animate-ping" style={{ animationDelay: '0.6s' }} />
      </div>

      <h3 className="text-2xl font-bold text-gray-900 mb-2">{t('payment.paymentSuccessful')}</h3>
      <p className="text-gray-600 mb-6">
        {t('payment.contractReady')}
      </p>

      <Button onClick={onClose} fullWidth style={primaryColor ? { background: primaryColor } : undefined}>
        {t('payment.downloadContract')}
      </Button>
    </div>
  );
};

const PaymentModal = ({
  isOpen,
  onClose,
  clientSecret,
  onSuccess,
  onError,
  price,
  currency,
  productName = 'Rental Agreement',
  countryName = 'Document',
  userEmail = '',
  isGuest = true,
}) => {
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const [email, setEmail] = useState(userEmail);
  const { t } = useTranslation();
  const { theme } = useCountry();
  const colors = theme?.colors || {};
  const primaryColor = colors.primary || '#2563eb';

  if (!isOpen) return null;

  const getCurrencySymbol = (curr) => {
    const symbols = { EUR: '€', GBP: '£', USD: '$', CAD: 'CA$', AUD: 'A$' };
    return symbols[curr] || '$';
  };

  const handleSuccess = (paymentIntent) => {
    setPaymentSucceeded(true);
    // Pass email along with paymentIntent for guest users
    onSuccess && onSuccess(paymentIntent, email);
  };

  const handleClose = () => {
    setPaymentSucceeded(false);
    onClose();
  };

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: primaryColor,
        colorBackground: '#f9fafb',
        colorText: '#1f2937',
        colorDanger: '#ef4444',
        fontFamily: 'Inter, system-ui, sans-serif',
        borderRadius: '12px',
        spacingUnit: '4px',
      },
      rules: {
        '.Input': {
          border: '2px solid #e5e7eb',
          boxShadow: 'none',
        },
        '.Input:focus': {
          border: `2px solid ${primaryColor}`,
          boxShadow: `0 0 0 3px ${primaryColor}1a`,
        },
        '.Label': {
          fontWeight: '500',
          marginBottom: '6px',
        },
      },
    },
  };

  return (
    <>
      {/* Backdrop — no click-to-close to prevent accidental dismissal mid-payment */}
      <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 animate-fade-in" />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto pointer-events-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 border-b border-gray-100">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('payment.completePayment')}</h2>
              <p className="text-sm text-gray-500 mt-1">{t('payment.secureCheckout')}</p>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {paymentSucceeded ? (
              <PaymentSuccess onClose={handleClose} primaryColor={primaryColor} />
            ) : (
              <>
                {/* Order Summary */}
                <div className="rounded-xl p-4 mb-6" style={{ background: `linear-gradient(to bottom right, #f9fafb, ${primaryColor}0d)` }}>
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('payment.orderSummary')}</h3>
                  <div className="space-y-2">
                    <OrderItem
                      label={`${countryName} ${productName}`}
                      value={`${getCurrencySymbol(currency)}${price || '29.99'}`}
                    />
                    <div className="border-t border-gray-200 my-2 pt-2">
                      <OrderItem
                        label={t('payment.total')}
                        value={`${getCurrencySymbol(currency)}${price || '29.99'}`}
                        highlight
                      />
                    </div>
                  </div>
                </div>

                {/* What You Get */}
                <div className="mb-6">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('payment.whatYouReceive')}</h3>
                  <div className="space-y-2">
                    {[
                      t('payment.legallyCompliant'),
                      t('payment.pdfReady'),
                      t('payment.editableWord'),
                      t('payment.freeUpdates')
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                        <svg className="w-4 h-4 flex-shrink-0" style={{ color: primaryColor }} fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Payment Form */}
                {clientSecret ? (
                  <Elements stripe={stripePromise} options={options}>
                    <PaymentForm
                      clientSecret={clientSecret}
                      onSuccess={handleSuccess}
                      onError={onError}
                      onCancel={handleClose}
                      price={price}
                      currency={currency}
                      productName={productName}
                      email={email}
                      onEmailChange={setEmail}
                      showEmailInput={isGuest && !userEmail}
                      primaryColor={primaryColor}
                    />
                  </Elements>
                ) : (
                  <div className="text-center py-8">
                    <Spinner size="lg" label={t('payment.preparingPayment')} />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Security Footer */}
          {!paymentSucceeded && (
            <div className="px-6 py-4 bg-gray-50 rounded-b-2xl border-t border-gray-100">
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
                <SecurityBadge
                  icon={
                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  }
                  text={t('payment.sslEncrypted')}
                />
                <SecurityBadge
                  icon={
                    <svg className="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  }
                  text={t('payment.secureCheckoutBadge')}
                />
                <SecurityBadge
                  icon={
                    <svg className="w-4 h-4 text-gray-500" viewBox="0 0 32 32" fill="currentColor">
                      <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697.309 13.134.309c-2.627 0-4.791.733-6.314 2.12C5.297 3.828 4.482 5.5 4.482 7.593c0 3.68 2.276 5.29 6.012 6.636 2.458.89 3.235 1.52 3.235 2.567 0 1.013-.817 1.631-2.386 1.631-1.936 0-4.9-.859-6.89-2.075l-.89 5.579c1.67.91 4.76 1.731 7.846 1.731 2.744 0 5.002-.716 6.593-2.09 1.743-1.499 2.627-3.63 2.627-6.256-.014-3.763-2.317-5.388-6.654-6.957zM28.5 16.5l-2.91 14.25h5.41l3-17.25H28.5z"/>
                    </svg>
                  }
                  text={t('payment.poweredByStripe')}
                />
              </div>

              {/* Money-back guarantee */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  <span className="font-medium">{t('payment.moneyBackGuarantee')}</span> {t('payment.notSatisfied')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentModal;
